import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Container,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CustomPagination from "./common/Pagination";
import { CustomToolbar } from "./common/CustomToolbar";
import _ from "lodash";

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  '& .MuiDataGrid-columnHeader[data-field="weeklyChange"]': {
    borderRight: "2px dotted #167bc3",
  },
  '& .MuiDataGrid-cell[data-field="weeklyChange"]': {
    borderRight: "2px dotted #167bc3",
  },
  "& .MuiDataGrid-cell": {
    textAlign: "center",
  },
  "& ::-webkit-scrollbar": {
    "border-top": "1px solid #e9e9ec",
    height: "7px",
    width: "7px",
  },
  "& ::-webkit-scrollbar-thumb:horizontal": {
    "border-radius": "15px",
    "background-color": "#6d6d6dc9",
  },
  "& ::-webkit-scrollbar-thumb:vertical": {
    "border-radius": "15px",
    "background-color": "#6d6d6dc9",
  },
  "& .MuiDataGrid-scrollbar--horizontal": {
    display: "block",
  },
  overflowX: "auto",
}));

export default function WeeklyReportPreview({ data, loading, dataGridRef }) {
  const [selectedDate, setSelectedDate] = useState("");
  const [filterModel, setFilterModel] = useState({
    items: [],
  });

  const parsedData = data.map((cur, index) => {
    const total = Object.values(cur?.trapsites || {}).reduce((pre, cur) => {
      return pre + (cur?.sum || 0);
    }, 0);
    return { ...cur, total, id: index };
  });

  const defaultDate =
    parsedData.length > 0 ? parsedData[parsedData.length - 1].date : "";

  useEffect(() => {
    if (defaultDate && !selectedDate) {
      setSelectedDate(defaultDate);
    }
  }, [defaultDate, selectedDate]);

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };

  const selectedData = parsedData.find((entry) => entry.date === selectedDate);

  const speciesSet = new Set();
  if (selectedData) {
    Object.values(selectedData.trapsites).forEach((trapsite) => {
      if (trapsite.species) {
        trapsite.species.forEach((species) => {
          speciesSet.add(species.species);
        });
      }
    });
  }

  const speciesArray = Array.from(speciesSet);
  const uniqueSpecies = speciesArray.sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );

  let flattenedData = [];

  data.forEach((entry) => {
    if (entry.trapsites) {
      Object.entries(entry.trapsites).forEach(([trapSiteName, trapData]) => {
        let row = {
          date: entry.date,
          trapSite: trapSiteName,
          sum: trapData.sum || 0,
          weeklyChange: trapData.weeklyChange || 0,
        };

        // Add species counts to the row
        (trapData.species || []).forEach((species) => {
          row[species.species] = species.count || 0;
        });

        flattenedData.push(row);
      });
    }
  });

  const allColumns = new Set();
  flattenedData.forEach((row) => {
    Object.keys(row).forEach((col) => allColumns.add(col));
  });
  flattenedData = flattenedData.filter((cur) => {
    return cur?.sum > 0;
  });

  const fixedColumns = ["date", "trapSite", "sum", "weeklyChange"];
  const finalColumnOrder = [
    ...fixedColumns,
    ...Array.from(allColumns)
      .filter((col) => !fixedColumns.includes(col))
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
  ];

  flattenedData = flattenedData.map((row) => {
    const reorderedItem = {};
    finalColumnOrder.forEach(
      (col) => (reorderedItem[col] = row[col] !== undefined ? row[col] : 0)
    );
    return reorderedItem;
  });

  const rowsData = selectedData
    ? Object.keys(selectedData.trapsites)
      .sort((a, b) => {
        if (a === "undefined") return 1;
        if (b === "undefined") return -1;
        return a.localeCompare(b, undefined, { sensitivity: "base" });
      })
      .map((trapSite, index) => {
      const trapData = selectedData.trapsites[trapSite];
      const row = {
        id: index,
        trapSite: trapSite,
        sum: trapData.sum || "No Data",
        weeklyChange: trapData.sum > 0 ? trapData.weeklyChange : "No Data",
      };
      uniqueSpecies.forEach((speciesName) => {
        row[speciesName] = 0;
      });

      if (trapData.species) {
        trapData.species.forEach((species) => {
          if (uniqueSpecies.includes(species.species)) {
            row[species.species] = species.count;
          }
        });
      }
      return row;
    })
    : [];

  const columns = [
    {
      field: "trapSite",
      headerName: "Trap Site",
      width: 250,
      headerAlign: "center",
    },
    {
      field: "sum",
      headerName: "Total_Count",
      width: 150,
      headerAlign: "center",
    },
    {
      field: "weeklyChange",
      headerName: "Weekly Change",
      width: 200,
      headerAlign: "center",
    },
    ...uniqueSpecies.map((speciesName) => ({
      field: speciesName,
      headerName: _.capitalize(speciesName),
      width: 150,
      headerAlign: "center",
    })),
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: "16px",
        }}
      >
        <FormControl margin="normal" sx={{ width: "250px" }}>
          <InputLabel>Start Week</InputLabel>
          <Select value={selectedDate} onChange={handleDateChange}>
            {parsedData.map((cur) => (
              <MenuItem key={cur.date} value={cur.date}>
                {cur.date}
                {cur.total > 0 ? "" : " (No Trapping)"}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div
        style={{
          paddingTop: "1rem",
          paddingBottom: "1rem",
          fontSize: "11px",
          width: "100%",
          height: "100%",
        }}
      >
        <Container
          sx={{
            width: "100%",
            height: "100%",
          }}
        >
          <StyledDataGrid
            rows={rowsData}
            columns={columns}
            loading={loading}
            localeText={{ noRowsLabel: "No Data" }}
            slots={{
              toolbar: CustomToolbar,
              pagination: CustomPagination,
            }}
            showToolbar
            filterModel={filterModel}
            onFilterModelChange={(newModel) => setFilterModel(newModel)}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 20,
                },
              },
              columns: {
                columnVisibilityModel: {
                  empty: false,
                  Unsure: false,
                },
              },
            }}
          />
        </Container>

        <div
          style={{
            height: "1px",
            width: "1px",
            overflow: "auto",
          }}
        >
          <DataGrid
            apiRef={dataGridRef}
            rows={flattenedData.map((cur, index) => {
              return { ...cur, id: index };
            })}
            columns={columns}
            loading={loading}
            localeText={{ noRowsLabel: "No Data" }}
            slots={{
              toolbar: CustomToolbar,
            }}
            filterModel={filterModel}
            onFilterModelChange={(newModel) => setFilterModel(newModel)}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  empty: false,
                  Unsure: false,
                },
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}
